// Tabla de la Cotización con reparto (neto | se financia | se paga hoy). Puro.
import { shownDiscountCode } from '@/shared/patterns/SalePromotion/discountCode';
import { anchorLineOf } from './buildSale';
import type { ChargeLine } from './saleCharge';
import type { WizardSession } from './types';

export interface QuoteTableRow {
    line: ChargeLine;
    netCents: number;
    /** Precio antes del descuento de Promociones; `null` sin descuento. */
    originalCents: number | null;
    discountCode?: string;
    gift: boolean;
    /** `null` = no se financia nada de la línea. */
    financedCents: number | null;
    /** `null` = no se paga nada hoy por la línea. */
    cashCents: number | null;
}

/** Descuento de Promociones en una línea: precio antes del descuento (`null` sin monto) y código. */
export interface LineDiscount {
    originalCents: number | null;
    discountCode?: string;
}

export interface QuoteTable {
    rows: QuoteTableRow[];
    netCents: number;
    financedCents: number;
    cashCents: number;
    /** Algún extra financiable no cupo entero en el tope del 30 % del equipo. */
    capFilled: boolean;
}

/** Filas de la tabla a partir de las líneas con reparto; un regalo va siempre en $0. */
export function quoteTableOf(session: WizardSession, lines: ChargeLine[]): QuoteTable {
    const reserved = new Map((session.reservedLines ?? []).map((l) => [l.lineId, l]));
    const anchorId = anchorLineOf(session)?.lineId;
    let capFilled = false;

    const rows = lines.map((line): QuoteTableRow => {
        const source = line.lineId === null ? undefined : reserved.get(line.lineId);
        const gift = source?.role === 'GIFT';
        const netCents = gift ? 0 : (line.totalCents ?? 0);
        const discounted = (source?.discountCents ?? 0) > 0;
        const originalCents = discounted
            ? (source?.originalPriceCents ?? (line.unitPriceCents ?? 0) * line.qty)
            : null;
        const isExtra = source !== undefined && source.lineId !== anchorId && !gift;
        if (isExtra && source.financeable && line.financedCents < netCents) capFilled = true;
        const cash = gift ? 0 : (line.cashCents ?? 0);
        const discountCode = source ? shownDiscountCode(source) : undefined;
        return {
            line,
            netCents,
            originalCents: gift ? (source.originalPriceCents ?? null) : originalCents,
            ...(discountCode !== undefined && { discountCode }),
            gift,
            financedCents: !gift && line.financedCents > 0 ? line.financedCents : null,
            cashCents: cash > 0 ? cash : null,
        };
    });

    const sum = (pick: (r: QuoteTableRow) => number | null): number =>
        rows.reduce((total, r) => total + (pick(r) ?? 0), 0);
    return {
        rows,
        netCents: sum((r) => r.netCents),
        financedCents: sum((r) => r.financedCents),
        cashCents: sum((r) => r.cashCents),
        capFilled,
    };
}

/** Descuento y código de cada línea cobrada, por su `key`, como los muestra la Cotización. */
export function lineDiscountsOf(
    session: WizardSession,
    lines: ChargeLine[],
): Map<string, LineDiscount> {
    return new Map(
        quoteTableOf(session, lines)
            .rows.filter(
                (r) => !r.gift && (r.originalCents !== null || r.discountCode !== undefined),
            )
            .map((r) => [
                r.line.key,
                {
                    originalCents: r.originalCents,
                    ...(r.discountCode !== undefined && { discountCode: r.discountCode }),
                },
            ]),
    );
}
